/**
 * Setting your own password, from your own profile.
 *
 * This is where 「あとから変更できます」 has to be true. An operator can hand out a
 * password when they create an account - the JMC and clinic add dialogs both ask
 * for one now - and until this screen existed there was nowhere for the person
 * to change what somebody else had chosen for them.
 *
 * Three rules the server enforces and this states before it is refused:
 *
 *   THE CURRENT PASSWORD, when one exists. The session proves possession, the
 *   old password proves knowledge, and a takeover needs both - so a stolen
 *   session cannot lock the owner out.
 *
 *   A RECENT SIGN-IN. `POST /auth/password` is behind `requireStepUp`, so a
 *   cookie found on an unlocked laptop hours later is not enough. The refusal is
 *   turned into a sentence here rather than the raw 「step_up_required」.
 *
 *   EVERY OTHER SESSION ENDS, including this one. That is what makes a change
 *   after a suspected compromise worth anything, and it is said BEFORE the
 *   press, not discovered as a sudden sign-out.
 */
import { useState, type FormEvent } from 'react';
import { ApiError, post } from '../api.ts';
import { Card, Pill } from './ui.tsx';
import { Field } from './EditPanel.tsx';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../../../shared/password-policy.ts';

export function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm !== '' && confirm !== next;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await post('/auth/password', {
        ...(hasPassword ? { currentPassword: current } : {}),
        newPassword: next,
      });
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      /*
       * The two refusals worth translating. `step_up_required` is not a fault
       * the person can act on as written - it means "sign in again first" - and
       * the rest come back as the server's own sentence, which is already in
       * their language.
       */
      if (err instanceof ApiError && err.code === 'step_up_required') {
        setError(
          '安全のため、ログインし直してからもう一度お試しください（最後のログインから時間が経過しています）。',
        );
      } else {
        setError(
          err instanceof ApiError
            ? [err.message, ...err.fields].join(' / ')
            : 'パスワードを変更できませんでした。',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="パスワード"
      caption={
        hasPassword
          ? 'メールアドレスとパスワードでログインできます。変更すると、ログイン中のセッションはすべて終了します。'
          : 'いまはGoogleログインのみです。パスワードを設定すると、メールアドレスとパスワードでもログインできます。'
      }
    >
      <p style={{ marginTop: 0 }}>
        {hasPassword ? (
          <Pill tone="ok">設定済み</Pill>
        ) : (
          <Pill tone="muted">未設定（Googleログインのみ）</Pill>
        )}
      </p>

      {done && (
        <p className="notice" role="status">
          パスワードを変更しました。ほかの端末のログインはすべて終了しています。
        </p>
      )}
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <form className="editpanel__body" onSubmit={(e) => void submit(e)}>
        {hasPassword && (
          <Field
            label="現在のパスワード"
            htmlFor="pw-current"
            required
            span="wide"
            hint="ご本人確認のために必要です。"
          >
            <input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              required
              maxLength={200}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
        )}

        <Field
          label="新しいパスワード"
          htmlFor="pw-new"
          required
          span="wide"
          hint={`${PASSWORD_HINT}。長さだけが条件です。記号や数字の指定はありません（覚えやすい文をそのまま使えます）。`}
        >
          <input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={200}
            placeholder={PASSWORD_HINT}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>

        <Field
          label="新しいパスワード（確認）"
          htmlFor="pw-confirm"
          required
          span="wide"
          hint={mismatch ? '⚠ 上のパスワードと一致していません。' : '同じものをもう一度入力してください。'}
        >
          <input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            required
            maxLength={200}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        <div className="editpanel__actions field--full">
          <button
            className="btn btn--sm"
            type="submit"
            disabled={busy || mismatch || next.length < MIN_PASSWORD_LENGTH}
          >
            {busy ? '変更中…' : hasPassword ? 'パスワードを変更する' : 'パスワードを設定する'}
          </button>
          <span className="muted small">
            変更すると、ほかの端末を含めてログイン中のセッションはすべて終了します。
          </span>
        </div>
      </form>
    </Card>
  );
}
